import { getRtdbInstance, getFirestoreUtils } from '@/lib/firebase'
import { useAuthStore } from '@/stores/authStore'
import type { ChatMessage, ChatMessagePayload } from '@/lib/sharedTypes'

// ─── 헬퍼 ───

function getUserInfo() {
  const user = useAuthStore.getState().user
  if (!user) throw new Error('로그인 필요')
  return { uid: user.uid, displayName: user.displayName, photoURL: user.photoURL, email: user.email }
}

// Reactions 포맷 변환: UI(string[]) ↔ RTDB(Record<string,true>)
export function reactionsToRtdb(reactions: Record<string, string[]>): Record<string, Record<string, true>> | null {
  const result: Record<string, Record<string, true>> = {}
  let hasAny = false
  for (const [emoji, uids] of Object.entries(reactions)) {
    if (uids.length > 0) {
      result[emoji] = {}
      for (const uid of uids) result[emoji][uid] = true
      hasAny = true
    }
  }
  return hasAny ? result : null
}

export function reactionsFromRtdb(rtdb: Record<string, Record<string, true>> | null | undefined): Record<string, string[]> {
  if (!rtdb) return {}
  const result: Record<string, string[]> = {}
  for (const [emoji, uidMap] of Object.entries(rtdb)) {
    if (uidMap && typeof uidMap === 'object') {
      result[emoji] = Object.keys(uidMap)
    }
  }
  return result
}

export function snapshotToChatMessage(key: string, val: Record<string, unknown>): ChatMessage {
  return {
    ...val,
    id: key,
    reactions: reactionsFromRtdb(val.reactions as Record<string, Record<string, true>> | null),
    mentions: val.mentions ? (Array.isArray(val.mentions) ? val.mentions : Object.values(val.mentions)) : [],
  } as ChatMessage
}

// Firestore 그룹 미리보기 업데이트 (백그라운드)
async function updateGroupPreview(groupId: string, payload: ChatMessagePayload, userName: string, now: string) {
  try {
    const { firestore, doc, updateDoc } = await getFirestoreUtils()
    const previewText = payload.type === 'image' ? '사진'
      : payload.type === 'video' ? '동영상'
      : payload.type === 'voice' ? '음성 메시지'
      : payload.type === 'file' ? `파일: ${payload.fileName || '파일'}`
      : payload.type === 'task_card' ? `할일: ${payload.taskSnapshot?.title || ''}`
      : payload.type === 'memo_card' ? `메모: ${payload.memoSnapshot?.title || ''}`
      : payload.type === 'system' ? ''
      : payload.text
    updateDoc(doc(firestore, `sharedGroups/${groupId}`), {
      lastChatText: (previewText || '').slice(0, 100),
      lastChatSenderName: userName,
      lastChatAt: now,
      lastChatType: payload.type || 'text',
      lastActivityAt: now,
    }).catch(() => {})
  } catch { /* 미리보기 실패 무시 */ }
}

// ─── 채팅 메시지 ───

export async function sendChatMessage(groupId: string, payload: ChatMessagePayload, msgId?: string): Promise<string> {
  const { ref, push, set } = await import('firebase/database')
  const db = await getRtdbInstance()
  const user = getUserInfo()
  const now = new Date().toISOString()

  const msgRef = msgId
    ? ref(db, `chatGroups/${groupId}/messages/${msgId}`)
    : push(ref(db, `chatGroups/${groupId}/messages`))

  const msgData = {
    userId: user.uid,
    userName: user.displayName || '(알 수 없음)',
    userPhotoURL: user.photoURL || '',
    text: payload.text,
    type: payload.type || 'text',
    mentions: payload.mentions || null,
    taskRef: payload.taskRef || null,
    imageUrl: payload.imageUrl || null,
    imageWidth: payload.imageWidth || null,
    imageHeight: payload.imageHeight || null,
    imageName: payload.imageName || null,
    taskSnapshot: payload.taskSnapshot || null,
    memoSnapshot: payload.memoSnapshot || null,
    replyTo: payload.replyTo || null,
    systemEvent: payload.systemEvent || null,
    voiceUrl: payload.voiceUrl || null,
    voiceDuration: payload.voiceDuration || null,
    fileUrl: payload.fileUrl || null,
    fileName: payload.fileName || null,
    fileSize: payload.fileSize || null,
    fileType: payload.fileType || null,
    linkPreview: payload.linkPreview || null,
    reactions: null,
    createdAt: now,
  }

  await set(msgRef, msgData)
  updateGroupPreview(groupId, payload, user.displayName || '', now)
  return msgRef.key!
}

export async function deleteChatMessage(groupId: string, messageId: string): Promise<void> {
  const { ref, update } = await import('firebase/database')
  const db = await getRtdbInstance()
  await update(ref(db, `chatGroups/${groupId}/messages/${messageId}`), {
    isDeleted: true,
    text: '',
    imageUrl: null, imageWidth: null, imageHeight: null, imageName: null,
    fileUrl: null, fileName: null, fileSize: null, fileType: null,
    voiceUrl: null, voiceDuration: null,
    taskSnapshot: null, taskRef: null, replyTo: null, mentions: null,
  })
}

// ─── 리액션 ───

export async function addChatReaction(groupId: string, messageId: string, emoji: string): Promise<void> {
  const { ref, set } = await import('firebase/database')
  const db = await getRtdbInstance()
  const user = getUserInfo()
  await set(ref(db, `chatGroups/${groupId}/messages/${messageId}/reactions/${emoji}/${user.uid}`), true)
}

export async function removeChatReaction(groupId: string, messageId: string, emoji: string): Promise<void> {
  const { ref, remove } = await import('firebase/database')
  const db = await getRtdbInstance()
  const user = getUserInfo()
  await remove(ref(db, `chatGroups/${groupId}/messages/${messageId}/reactions/${emoji}/${user.uid}`))
}

// ─── 시스템 메시지 ───

export async function sendSystemMessage(groupId: string, action: string, actorName?: string, targetTitle?: string): Promise<void> {
  const { ref, push, set } = await import('firebase/database')
  const db = await getRtdbInstance()
  const now = new Date().toISOString()

  const msgRef = push(ref(db, `chatGroups/${groupId}/messages`))
  await set(msgRef, {
    userId: 'system',
    userName: '시스템',
    userPhotoURL: '',
    text: '',
    type: 'system',
    systemEvent: { action, actorName: actorName || '', targetTitle: targetTitle || '' },
    reactions: null,
    createdAt: now,
  })

  updateGroupPreview(groupId, { text: '', type: 'system' }, '', now)
}

// ─── 읽음 위치 ───

export async function updateChatReadPosition(groupId: string): Promise<void> {
  const { ref, set } = await import('firebase/database')
  const db = await getRtdbInstance()
  const user = getUserInfo()
  const now = new Date().toISOString()
  // RTDB: 안 읽음 뱃지 계산용
  await set(ref(db, `chatGroups/${groupId}/readPositions/${user.uid}`), {
    lastReadAt: now,
  })
  // Firestore: 메시지별 읽음 수 계산용
  try {
    const { firestore, doc, updateDoc } = await getFirestoreUtils()
    updateDoc(doc(firestore, `sharedGroups/${groupId}/members/${user.uid}`), {
      lastReadChatAt: now,
    })
  } catch { /* 백그라운드 — 실패해도 RTDB는 이미 갱신됨 */ }
}

// ─── 타이핑 ───

export async function updateTypingStatus(groupId: string, isTyping: boolean): Promise<void> {
  const { ref, set, remove, onDisconnect } = await import('firebase/database')
  const db = await getRtdbInstance()
  const user = useAuthStore.getState().user
  if (!user) return
  const typingRef = ref(db, `chatGroups/${groupId}/typing/${user.uid}`)
  if (isTyping) {
    await set(typingRef, true)
    onDisconnect(typingRef).remove()
  } else {
    await remove(typingRef)
  }
}

// ─── 프레즌스 ───

export async function updatePresence(groupId: string, isOnline: boolean): Promise<void> {
  const { ref, set, onDisconnect } = await import('firebase/database')
  const db = await getRtdbInstance()
  const user = useAuthStore.getState().user
  if (!user) return
  const presRef = ref(db, `chatGroups/${groupId}/presence/${user.uid}`)
  if (isOnline) {
    await set(presRef, { isOnline: true, lastActiveAt: new Date().toISOString() })
    onDisconnect(presRef).set({ isOnline: false, lastActiveAt: new Date().toISOString() })
  } else {
    await set(presRef, { isOnline: false, lastActiveAt: new Date().toISOString() })
  }
}
